import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';

const MAX_REVIEW_IMAGES = 3;
const MAX_REVIEW_IMAGE_BYTES = 5 * 1024 * 1024;

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ filename: file.name, contentType: file.type, dataUrl: reader.result });
  reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
  reader.readAsDataURL(file);
});

export default function OrdersPage() {
  const { token, user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackDrafts, setFeedbackDrafts] = useState({});
  const [feedbackErrors, setFeedbackErrors] = useState({});
  const [feedbackSubmitting, setFeedbackSubmitting] = useState({});
  const [editingFeedback, setEditingFeedback] = useState({});
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());

  useEffect(() => {
    async function fetchOrders() {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch('/api/orders', {
          headers: { 'x-auth-token': token },
        });
        if (res.ok) {
          const data = await res.json();
          setOrders(data);
        }
      } catch (err) {
        console.error('Failed to fetch orders:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchOrders();
  }, [token]);

  if (!user && !token) {
    return (
      <div className="container py-5 text-center">
        <h5>Sign in to view your orders</h5>
        <Link to="/login" className="btn btn-primary-custom rounded-pill mt-3">
          Sign In with Google
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading orders...</span>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Delivered':
        return 'bg-success';
      case 'Shipped':
        return 'bg-info text-dark';
      case 'Packed':
      case 'Confirmed':
        return 'bg-primary';
      case 'Cancelled':
        return 'bg-danger';
      default:
        return 'bg-warning text-dark';
    }
  };

  const updateFeedbackDraft = (orderId, changes) => {
    setFeedbackDrafts((current) => ({
      ...current,
      [orderId]: {
        rating: 0,
        comment: '',
        files: [],
        ...current[orderId],
        ...changes,
      },
    }));
    setFeedbackErrors((current) => ({ ...current, [orderId]: '' }));
  };

  const startEditingFeedback = (order) => {
    setExpandedOrderIds((current) => new Set(current).add(order.id));
    setFeedbackDrafts((current) => ({
      ...current,
      [order.id]: {
        rating: order.feedback?.rating || 0,
        comment: order.feedback?.comment || '',
        files: [],
      },
    }));
    setEditingFeedback((current) => ({ ...current, [order.id]: true }));
    setFeedbackErrors((current) => ({ ...current, [order.id]: '' }));
  };

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const updateFeedbackFiles = (orderId, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length > MAX_REVIEW_IMAGES) {
      setFeedbackErrors((current) => ({ ...current, [orderId]: `Choose up to ${MAX_REVIEW_IMAGES} images.` }));
      return;
    }
    const invalidFile = files.find((file) => !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) || file.size > MAX_REVIEW_IMAGE_BYTES);
    if (invalidFile) {
      setFeedbackErrors((current) => ({ ...current, [orderId]: 'Use JPG, PNG, WEBP, or GIF images up to 5 MB each.' }));
      return;
    }
    updateFeedbackDraft(orderId, { files });
  };

  const submitFeedback = async (order) => {
    const draft = feedbackDrafts[order.id] || { rating: 0, comment: '', files: [] };
    const rating = Number(draft.rating);
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      setFeedbackErrors((current) => ({ ...current, [order.id]: 'Choose a rating from 1 to 5.' }));
      return;
    }

    setFeedbackSubmitting((current) => ({ ...current, [order.id]: true }));
    setFeedbackErrors((current) => ({ ...current, [order.id]: '' }));
    try {
      const files = await Promise.all((draft.files || []).map(readFileAsDataUrl));
      const res = await fetch(`/api/orders/${order.id}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify({ rating, comment: draft.comment || '', files }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || 'Unable to save feedback.');

      setOrders((current) => current.map((item) => (
        item.id === order.id ? { ...item, feedback: data.feedback } : item
      )));
      setEditingFeedback((current) => ({ ...current, [order.id]: false }));
    } catch (err) {
      setFeedbackErrors((current) => ({ ...current, [order.id]: err.message }));
    } finally {
      setFeedbackSubmitting((current) => ({ ...current, [order.id]: false }));
    }
  };

  const openInvoice = async (orderId) => {
    const invoiceWindow = window.open('', '_blank');
    if (!invoiceWindow) {
      alert('Please allow pop-ups to view the invoice.');
      return;
    }

    setInvoiceLoadingId(orderId);
    try {
      const res = await fetch(`/api/orders/${orderId}/invoice`, {
        headers: { 'x-auth-token': token },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.msg || 'Unable to generate invoice.');
      }
      const blobUrl = URL.createObjectURL(await res.blob());
      invoiceWindow.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
    } catch (error) {
      invoiceWindow.close();
      alert(error.message);
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  return (
    <div className="container py-4">
      <h1 className="brand-font mb-4">My Orders & Tracking</h1>

      {orders.length === 0 ? (
        <div className="text-center py-5 border rounded bg-card">
          <div className="fs-1 mb-2">📦</div>
          <h5>No orders placed yet</h5>
          <p className="text-muted small">Your order history and live tracking details will appear here.</p>
          <Link to="/shop" className="btn btn-primary-custom rounded-pill mt-2">
            Start Shopping →
          </Link>
        </div>
      ) : (
        <div className="d-flex flex-column gap-4">
          {orders.map((order) => {
            const isDelivered = String(order.status || '').toLowerCase() === 'delivered';
            const isExpanded = expandedOrderIds.has(order.id);
            return (
            <div id={`order-${order.id}`} key={order.id} className="border rounded bg-card p-4 shadow-sm">
              {isDelivered ? (
                <div className="d-flex align-items-center gap-2 py-1">
                  <button
                    type="button"
                    className="btn btn-link text-reset text-decoration-none text-start p-0 flex-grow-1"
                    style={{ minWidth: 0 }}
                    onClick={() => toggleOrderExpanded(order.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`order-details-${order.id}`}
                  >
                    <span className="me-2" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                    <span className="font-weight-bold">{order.order_number}</span>
                    <span className="text-muted small ms-2">{new Date(order.date).toLocaleDateString()}</span>
                  </button>
                  <span className={`badge ${getStatusBadge(order.status)}`}>{order.status}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary text-nowrap"
                    onClick={() => openInvoice(order.id)}
                    disabled={invoiceLoadingId === order.id}
                  >
                    {invoiceLoadingId === order.id ? 'Preparing…' : 'Invoice'}
                  </button>
                  <span className="font-weight-bold text-primary text-nowrap">₹{order.total_amount}</span>
                </div>
              ) : (
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-3 border-bottom gap-2">
                  <div>
                    <h6 className="font-weight-bold m-0">{order.order_number}</h6>
                    <span className="text-muted small">
                      Placed on {new Date(order.date).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openInvoice(order.id)}
                      disabled={invoiceLoadingId === order.id || order.status !== 'Delivered'}
                    >
                      {invoiceLoadingId === order.id ? 'Preparing…' : 'Invoice after delivery'}
                    </button>
                    <span className={`badge px-3 py-2 ${getStatusBadge(order.status)}`}>
                      {order.status}
                    </span>
                    <span className="font-weight-bold text-primary fs-5">
                      ₹{order.total_amount}
                    </span>
                  </div>
                </div>
              )}

              {(!isDelivered || isExpanded) && <div id={`order-details-${order.id}`}>
              {/* Status Timeline Progress Bar */}
              <div className="my-3 p-3 bg-background rounded">
                <span className="small font-weight-bold text-muted d-block mb-2">Delivery Timeline:</span>
                <div className="d-flex justify-content-between text-center small font-weight-bold">
                  <div className={['Pending', 'Confirmed', 'Packed', 'Shipped', 'Delivered'].includes(order.status) ? 'text-success' : 'text-muted'}>
                    ✓ Order Received
                  </div>
                  <div className={['Confirmed', 'Packed', 'Shipped', 'Delivered'].includes(order.status) ? 'text-success' : 'text-muted'}>
                    ✓ Confirmed
                  </div>
                  <div className={['Packed', 'Shipped', 'Delivered'].includes(order.status) ? 'text-success' : 'text-muted'}>
                    ✓ Packed
                  </div>
                  <div className={['Shipped', 'Delivered'].includes(order.status) ? 'text-success' : 'text-muted'}>
                    ✓ Shipped
                  </div>
                  <div className={order.status === 'Delivered' ? 'text-success' : 'text-muted'}>
                    ✓ Delivered
                  </div>
                </div>
              </div>

              {/* Shipping Address Details */}
              <div className="small text-muted">
                <strong>Shipping Address:</strong> {order.customer_name}, {order.shipping_address}, {order.shipping_city}, {order.shipping_state} {order.shipping_zip}
              </div>

              {isDelivered && (() => {
                const feedbackDraft = feedbackDrafts[order.id] || {
                  rating: order.feedback?.rating || 0,
                  comment: order.feedback?.comment || '',
                  files: [],
                };
                const isEditing = editingFeedback[order.id] || !order.feedback;
                const isSubmitting = feedbackSubmitting[order.id];

                return (
                  <section className="order-feedback-panel mt-4" aria-labelledby={`feedback-title-${order.id}`}>
                    {order.feedback && !isEditing ? (
                      <div className="order-feedback-submitted">
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                          <div>
                            <h6 id={`feedback-title-${order.id}`} className="mb-1">Thanks for your feedback</h6>
                            <div className="order-feedback-rating" aria-label={`${order.feedback.rating} out of 5 stars`}>
                              {'★'.repeat(order.feedback.rating)}
                              <span className="text-muted">{'★'.repeat(5 - order.feedback.rating)}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-custom"
                            onClick={() => startEditingFeedback(order)}
                          >
                            Update feedback
                          </button>
                        </div>
                        {order.feedback.comment && <p className="small text-muted mb-0 mt-2">{order.feedback.comment}</p>}
                        {order.feedback.images?.length > 0 && (
                          <div className="reviews-card__images mt-3" aria-label="Your review images">
                            {order.feedback.images.map((image) => <a key={image.id} href={image.url} target="_blank" rel="noopener noreferrer"><img src={image.url} alt={image.filename || 'Your review'} loading="lazy" /></a>)}
                          </div>
                        )}
                        {order.feedback.status === 'pending' && <p className="small text-muted mb-0 mt-2">Your updated review is awaiting approval before it appears publicly.</p>}
                      </div>
                    ) : (
                      <form onSubmit={(event) => { event.preventDefault(); submitFeedback(order); }}>
                        <h6 id={`feedback-title-${order.id}`} className="mb-1">How was your order?</h6>
                        <p className="small text-muted mb-2">Your order was delivered. Share a quick review.</p>
                        <div className="order-feedback-stars mb-3" role="group" aria-label="Rate your order from 1 to 5 stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              className={`order-feedback-star ${star <= feedbackDraft.rating ? 'is-selected' : ''}`}
                              aria-label={`${star} ${star === 1 ? 'star' : 'stars'}`}
                              aria-pressed={feedbackDraft.rating === star}
                              onClick={() => updateFeedbackDraft(order.id, { rating: star })}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        <label className="form-label small" htmlFor={`feedback-comment-${order.id}`}>Comment (optional)</label>
                        <textarea
                          id={`feedback-comment-${order.id}`}
                          className="form-control order-feedback-textarea"
                          maxLength={1000}
                          value={feedbackDraft.comment}
                          onChange={(event) => updateFeedbackDraft(order.id, { comment: event.target.value })}
                          placeholder="Tell us what you liked or what we can improve."
                        />
                        <label className="form-label small mt-3" htmlFor={`feedback-images-${order.id}`}>Photos (optional, up to 3)</label>
                        <input
                          id={`feedback-images-${order.id}`}
                          className="form-control"
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          multiple
                          onChange={(event) => updateFeedbackFiles(order.id, event.target.files)}
                        />
                        {feedbackDraft.files?.length > 0 && <p className="small text-muted mb-0 mt-2">{feedbackDraft.files.map((file) => file.name).join(', ')}</p>}
                        {feedbackErrors[order.id] && <div className="text-danger small mt-2" role="alert">{feedbackErrors[order.id]}</div>}
                        <div className="d-flex flex-wrap gap-2 mt-3">
                          <button type="submit" className="btn btn-primary-custom rounded-pill" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving…' : order.feedback ? 'Save changes' : 'Send feedback'}
                          </button>
                          {order.feedback && (
                            <button
                              type="button"
                              className="btn btn-outline-custom rounded-pill"
                              onClick={() => setEditingFeedback((current) => ({ ...current, [order.id]: false }))}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </form>
                    )}
                  </section>
                );
              })()}
              </div>}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
